// noinspection SpellCheckingInspection

import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useSession } from "users/__session/session-context";
import Proposal from "work/proposals/proposal";
import { MaskedTeam, Masked } from "work/_shared/masked-fields";

const AudienceLabel = styled("span")<{ isexternal: boolean; disabled?: boolean }>(
  ({ theme, isexternal, disabled }) => ({
    color: isexternal ? theme.palette.error.main : theme.palette.common.black,
    fontWeight: isexternal ? theme.typography.fontWeightBold : undefined,
    opacity: disabled ? 0.5 : 1,
  })
);
const HelperText = styled(FormHelperText)(() => ({
  textWrap: "wrap"
}));

type AudienceSwitchProps = {
  className?: string;
  proposal: Proposal;
  disabled?: boolean;
  isExternal?: boolean;
  onIsExternalChanged: (isExternal: boolean) => void;
};

export default function AudienceSwitch(props: Readonly<AudienceSwitchProps>) {
  const { disabled, proposal, onIsExternalChanged } = props;

  const [isExternal, setIsExternal] = useState(props.isExternal ?? false);
  const session = useSession();

  const isClient =
    MaskedTeam.isPresentNotMasked(proposal?.workAgreement.clientTeam) &&
    !!proposal.workAgreement.clientTeam?.leader?.userId.isEqualTo(session.user?.id);
  const isTeamLeader =
    Masked.isPresentNotMasked(proposal.vendorTeam) && !!proposal.vendorTeam.leader?.userId.isEqualTo(session.user?.id);

  useEffect(() => {
    setIsExternal(props.isExternal ?? false);
  }, [props.isExternal]);

  function handleIsExternalChanged() {
    onIsExternalChanged(!isExternal);
    setIsExternal((prev) => !prev);
  }

  if (proposal && !isClient && !isTeamLeader) return null;

  return (
    <Tooltip title={disabled ? "You cannot change a comment from external to internal" : ""}>
      <span>
        <FormControl component="span" variant="standard">
          <FormControlLabel
            control={
              <Switch
                checked={isExternal}
                disabled={disabled}
                color={isExternal ? "error" : undefined}
                onChange={handleIsExternalChanged}
              />
            }
            label={
              <AudienceLabel isexternal={isExternal} disabled={disabled}>
                External Comment
              </AudienceLabel>
            }
          />
          {isExternal && (
            <HelperText>
              <strong>Note:</strong> This comment will be visible to everyone associated with this proposal, including:
              <p>
                <i>the proposal creator, team, and both client and vendor reviewers</i>
              </p>
            </HelperText>
          )}
        </FormControl>
      </span>
    </Tooltip>
  );
}
